// src/utils/encryption.ts

export class EncryptionService {
  private saltLength = 32;   // 256 bits
  private ivLength = 12;     // 96 bits (AES-GCM 標準)
  private keyLength = 256;   // 256 bits
  private iterations = 100000; 

  // --- 1. 二進位轉 Base64 ---
  arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  // --- 2. Base64 轉二進位 ---
  base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  // --- 3. 產生隨機 Salt ---
  generateSalt(): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(this.saltLength));
  }

  // --- 4. 產生隨機 IV ---
  generateIV(): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(this.ivLength));
  }

  // --- 5. 從密碼和 Salt 派生金鑰 ---
  async deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: this.iterations,
        hash: 'SHA-256'
      },
      keyMaterial,
      {
        name: 'AES-GCM',
        length: this.keyLength
      },
      false,
      ['encrypt', 'decrypt']
    );
  }

  // --- 6. 高階整合加密函數 ---
  async encryptNote(plainText: string, masterPassword: string): Promise<{
    cipherTextBase64: string;
    ivBase64: string;
    saltBase64: string;
  }> {
    const salt = this.generateSalt();
    const iv = this.generateIV();
    const key = await this.deriveKey(masterPassword, salt);
    const encoder = new TextEncoder();
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv },
      key,
      encoder.encode(plainText)
    );

    return {
      cipherTextBase64: this.arrayBufferToBase64(encrypted),
      ivBase64: this.arrayBufferToBase64(iv.buffer),
      saltBase64: this.arrayBufferToBase64(salt.buffer)
    };
  }

  // --- 7. 高階整合解密函數 ---
  async decryptNote(
    cipherTextBase64: string,
    masterPassword: string,
    saltBase64: string,
    ivBase64: string
  ): Promise<string> {
    try {
      const saltBuffer = new Uint8Array(this.base64ToArrayBuffer(saltBase64));
      const ivBuffer = new Uint8Array(this.base64ToArrayBuffer(ivBase64));
      const encryptedBuffer = this.base64ToArrayBuffer(cipherTextBase64);
      const key = await this.deriveKey(masterPassword, saltBuffer);
      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: ivBuffer },
        key,
        encryptedBuffer
      );
      return new TextDecoder().decode(decrypted);
    } catch (error: any) {
      console.error('解密失敗詳細日誌:', error);
      throw new Error('解密失敗: ' + (error.message || '密鑰或密文損壞'));
    }
  }

  // --- 8. 密碼雜湊 ---
  async hashPassword(password: string, salt: Uint8Array): Promise<string> {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      'PBKDF2',
      false,
      ['deriveBits']
    );

    const hashBuffer = await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: this.iterations,
        hash: 'SHA-256'
      },
      keyMaterial,
      256
    );

    return this.arrayBufferToBase64(hashBuffer);
  }

  // ==========================================
  // ✅ 新增：Note Key 相關輔助函數
  // ==========================================

  /**
   * 生成隨機 Note Key（256 bits）
   */
  generateNoteKey(): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(32));
  }

  /**
   * 匯入 Note Key
   */
  async importNoteKey(keyBuffer: ArrayBuffer): Promise<CryptoKey> {
    return await crypto.subtle.importKey(
      'raw',
      new Uint8Array(keyBuffer),
      { name: 'AES-GCM' },
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * 用 Note Key 加密
   */
  async encryptWithNoteKey(data: string, noteKey: CryptoKey): Promise<string> {
    if (!data) return '';
    const encoder = new TextEncoder();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv },
      noteKey,
      encoder.encode(data)
    );
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encrypted), iv.length);
    return btoa(String.fromCharCode(...combined));
  }

  /**
   * 用 Note Key 解密
   */
  async decryptWithNoteKey(base64Data: string, noteKey: CryptoKey): Promise<string> {
    if (!base64Data) return '';
    try {
      const combined = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
      if (combined.length < 13) return '';
      const iv = combined.slice(0, 12);
      const encrypted = combined.slice(12);
      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv },
        noteKey,
        encrypted
      );
      return new TextDecoder().decode(decrypted);
    } catch (e) {
      console.warn('Note Key 解密失敗:', e);
      return '';
    }
  }

  /**
   * 用 DEK 加密 Note Key
   */
  async encryptNoteKeyWithDEK(noteKeyBuffer: ArrayBuffer, dek: CryptoKey): Promise<string> {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv },
      dek,
      noteKeyBuffer
    );
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encrypted), iv.length);
    return btoa(String.fromCharCode(...combined));
  }

  /**
   * 用 DEK 解密 Note Key
   */
  async decryptNoteKeyWithDEK(base64Data: string, dek: CryptoKey): Promise<ArrayBuffer> {
    const combined = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);
    return await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv },
      dek,
      encrypted
    );
  }

  /**
   * 從密碼派生金鑰（用於分享密碼）
   */
  async deriveKeyFromPassword(password: string): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      'PBKDF2',
      false,
      ['deriveKey']
    );
    return await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: encoder.encode('share-auto-salt-v1'),
        iterations: 100000,
        hash: 'SHA-256'
      },
      keyMaterial,
      {
        name: 'AES-GCM',
        length: 256
      },
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * 生成隨機分享密碼（8 位大寫字母 + 數字）
   */
  generateSharePassword(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }
}